import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTransportRequestList } from '../../../../api/transports';
import LoadingAnimation from '../../../../reusable-components/loader/LoadingAnimation';
import Iconify from '../../../../reusable-components/iconify';
import { fDayOfWeek, fHoursMinutes, formatDateTimeWithoutTimezone } from '../../../../utils/formatTime';
import { fToTitleMultipleWords } from '../../../../utils/formatText';
import { getDayRange, getVisibleRange, getWeekRange } from '../../../../utils/getDateRange';
import { TRIP_DIRECTION, TIME_GRID_DAY, TIME_GRID_WEEK, DAY_GRID_MONTH, LIST_WEEK } from '../constants';
import { REQUEST_STATUS_NAMES, getStatusColor, getStatusHoverColor } from '../statusEnums';
import CalendarEventTooltip from './CalendarEventTooltip';
import { applyMonthDayExpansion } from './applyMonthDayExpansion';
import { getCalendarOptionsSx } from './CalendarOptionsSx';
import { useCalendarHoverTooltip } from './useCalendarHoverTooltip';

const HOVER_TOOLTIP_DELAY_MS = 250;
const TOOLTIP_WIDTH_PX = 335;

const formatEventTime = (dateValue) => fHoursMinutes(dateValue, 'h:mm a');

const formatListDayLabel = (dateValue) => {
  if (!dateValue) return 'No Apt. Time';

  const date = new Date(dateValue);
  const month = date.toLocaleDateString('en-US', { month: 'long' }).toUpperCase();
  const day = date.getDate();
  const weekday = fDayOfWeek(date, 'EEE');

  return `${month} ${day}, ${weekday}`;
};

const renderWeekViewDayHeader = (dateValue) => {
  const date = new Date(dateValue);
  const weekday = fDayOfWeek(date, 'EEE').toUpperCase();
  const dayOfMonth = String(date.getDate());

  return (
    <div className="fc-week-header-wrap" aria-hidden="true">
      <span className="fc-week-header-weekday">{weekday}</span>
      <span className="fc-week-header-date">{dayOfMonth}</span>
    </div>
  );
};

const renderAllDayIcon = (arg) => {
  if (arg?.view?.type === LIST_WEEK) {
    return null;
  }

  return (
    <Box className="fc-all-day-icon-wrap" aria-hidden="true" sx={{ color: 'text.secondary' }}>
      <Iconify
        icon="material-symbols:info-rounded"
        width={26}
        clickable={false}
        sx={{ transform: 'translateX(-1px)' }}
      />
    </Box>
  );
};

CalendarOptions.propTypes = {
  statusFilter: PropTypes.arrayOf(PropTypes.number),
  tripDirection: PropTypes.arrayOf(PropTypes.string),
  facilityFilter: PropTypes.arrayOf(PropTypes.number),
  selectedViewType: PropTypes.oneOf([TIME_GRID_DAY, TIME_GRID_WEEK, DAY_GRID_MONTH, LIST_WEEK]),
  onViewTypeChange: PropTypes.func,
};

export default function CalendarOptions({
  statusFilter,
  tripDirection,
  facilityFilter,
  selectedViewType,
  onViewTypeChange,
}) {
  const [allEvents, setAllEvents] = useState([]);
  const [expandedMonthDays, setExpandedMonthDays] = useState({});
  const calendarContainerRef = useRef();
  const calendarRef = useRef();
  const abortControllerRef = useRef();
  const [loading, setLoading] = useState(false);
  const [dayViewDateLabel, setDayViewDateLabel] = useState({ weekday: '', dayOfMonth: '' });
  const navigate = useNavigate();
  const location = useLocation();
  const activeViewType = selectedViewType || TIME_GRID_DAY;
  const { hoverTooltip, handleEventMouseEnter, handleEventMouseLeave } = useCalendarHoverTooltip({
    hoverDelayMs: HOVER_TOOLTIP_DELAY_MS,
    formatEventTime,
  });

  const getCalendarApi = useCallback(() => calendarRef.current?.getApi?.() || null, []);

  // Keep FullCalendar's internal view aligned with the selected view in React state.
  useEffect(() => {
    const calendarApi = getCalendarApi();
    if (calendarApi && calendarApi.view?.type !== activeViewType) {
      calendarApi.changeView(activeViewType);
    }
  }, [activeViewType, getCalendarApi]);

  // Restore calendar state if coming from request details
  useEffect(() => {
    const calendarState = location.state?.calendarState;
    if (calendarState) {
      const calendarApi = getCalendarApi();
      if (calendarApi && calendarState.viewType && calendarState.startDate) {
        onViewTypeChange?.(calendarState.viewType);
        calendarApi.changeView(calendarState.viewType, calendarState.startDate);
      }
    }
  }, [getCalendarApi, location.state, onViewTypeChange]);

  // Handle event click
  const handleEventClick = useCallback(
    (clickInfo) => {
      // Use the original requestId stored in extendedProps, not the unique event id
      const requestId = clickInfo.event.extendedProps.requestId || clickInfo.event.id;
      if (!requestId) return;

      const clickedDate = clickInfo.event.start
        ? clickInfo.event.allDay
          ? clickInfo.event.startStr
          : formatDateTimeWithoutTimezone(clickInfo.event.start)
        : null;

      // Get current calendar state
      const calendarApi = getCalendarApi();
      if (!calendarApi) return;
      const viewApi = calendarApi.view;
      const calendarState = {
        viewType: viewApi.type,
        startDate: calendarApi.getDate?.() || viewApi.currentStart,
        endDate: viewApi.activeEnd,
        date: clickedDate,
      };

      const occurrenceId = clickInfo.event.extendedProps.occurrenceId || null;
      const path = clickedDate
        ? `/dashboard/transports/request-details/${requestId}/${clickedDate}`
        : `/dashboard/transports/request-details/${requestId}`;

      navigate(path, {
        state: { calendarState, occurrenceId },
      });
    },
    [getCalendarApi, navigate]
  );

  // Fetch events for the current view
  const fetchEvents = useCallback(
    async (info) => {
      // Cancel previous request if still running
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setLoading(true);
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Determine date range based on the calendar view
      const currentViewType = info?.view?.type || activeViewType;

      if (currentViewType === TIME_GRID_DAY) {
        const weekday = info.start.toLocaleDateString([], { weekday: 'short' }).toUpperCase();
        setDayViewDateLabel({ weekday, dayOfMonth: String(info.start.getDate()) });
      } else {
        setDayViewDateLabel({ weekday: '', dayOfMonth: '' });
      }

      let range;

      if (currentViewType === TIME_GRID_DAY) {
        range = getDayRange(info.start);
      } else if (currentViewType === TIME_GRID_WEEK || currentViewType === LIST_WEEK) {
        range = getWeekRange(info.start);
      } else {
        range = getVisibleRange(info.start, info.end);
      }

      try {
        const response = await getTransportRequestList(range.startDate, range.endDate, {
          signal: abortController.signal,
        });

        const data = response.data || [];

        const formattedEvents = data
          .map((item) => {
            const eventDateTime = item.appointmentDateTime || item.seenByDate;
            if (!eventDateTime) {
              return null;
            }

            const hasAppointmentDateTime = Boolean(item.appointmentDateTime);
            const occurrenceKey = hasAppointmentDateTime
              ? new Date(eventDateTime).toISOString()
              : String(eventDateTime).split('T')[0];
            const eventStart = hasAppointmentDateTime ? new Date(eventDateTime) : occurrenceKey;

            const direction = item.direction === TRIP_DIRECTION.INBOUND ? 'IN' : 'OUT';
            const backgroundColor = getStatusColor(item.requestStatusId);
            const uniqueId = `${item.id}-${occurrenceKey}`;
            const requestTime = hasAppointmentDateTime ? formatEventTime(item.appointmentDateTime) : '';
            const residentNames = item.residentNames.map(fToTitleMultipleWords).join(', ');
            const directionLabel = item.direction === TRIP_DIRECTION.INBOUND ? 'Inbound Trip' : 'Outbound Trip';

            return {
              id: uniqueId,
              title: `${direction} ${requestTime} ${residentNames}: ${item.transportTypeName}`,
              start: eventStart,
              allDay: !hasAppointmentDateTime,
              backgroundColor,
              borderColor: backgroundColor,
              extendedProps: {
                requestId: item.id,
                linkedRequestId: item.linkedRequestId || null,
                requestStatusId: item.requestStatusId,
                statusColor: backgroundColor,
                hoverColor: getStatusHoverColor(item.requestStatusId),
                requestStatusName: REQUEST_STATUS_NAMES[item.requestStatusId],
                facilityId: item.facilityId || null,
                occurrenceId: item.occurrenceId || null,
                directionLabel,
                residentNames,
                transportTypeName: item.transportTypeName,
                appointmentDateTime: item.appointmentDateTime,
              },
              direction: item.direction,
            };
          })
          .filter(Boolean);

        setAllEvents(formattedEvents);
        setLoading(false);
      } catch (err) {
        // Only clear loading if this is the latest request (not aborted)
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [activeViewType]
  );

  const filteredEvents = useMemo(
    () =>
      allEvents.filter(
        (event) =>
          (!statusFilter || statusFilter.length === 0 || statusFilter.includes(event.extendedProps.requestStatusId)) &&
          (!tripDirection ||
            tripDirection.length === 0 ||
            (event.direction && tripDirection.includes(event.direction))) &&
          (!facilityFilter || facilityFilter.length === 0 || facilityFilter.includes(event.extendedProps.facilityId))
      ),
    [statusFilter, tripDirection, facilityFilter, allEvents]
  );

  const renderEventContent = useCallback((eventInfo) => {
    const title = eventInfo.event.title || '';
    const { appointmentDateTime, statusColor } = eventInfo.event.extendedProps;
    const firstSpaceIndex = title.indexOf(' ');
    const directionText = firstSpaceIndex > -1 ? title.slice(0, firstSpaceIndex) : title;
    const remainderText = firstSpaceIndex > -1 ? title.slice(firstSpaceIndex + 1).trim() : '';

    return (
      <Box
        component="span"
        sx={{
          display: eventInfo.view.type === LIST_WEEK ? 'flex' : 'inline-flex',
          gap: '4px',
          minWidth: 0,
          ...(eventInfo.view.type === LIST_WEEK
            ? {
                m: '3px 0',
                p: '3px 8px',
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: '4px',
                backgroundColor: statusColor,
                fontSize: '0.75rem',
              }
            : null),
        }}
      >
        <Box component="span">{directionText}</Box>
        {appointmentDateTime ? (
          <Box component="span">{remainderText}</Box>
        ) : (
          <>
            <Iconify
              className="fc-missing-appointment-icon"
              icon="mdi:exclamation"
              width={14}
              clickable={false}
              sx={{ alignSelf: 'center' }}
            />
            <Box component="span">{remainderText}</Box>
          </>
        )}
      </Box>
    );
  }, []);

  // When the visible range/view changes, sync the selected view and reload events.
  const handleDatesSet = useCallback(
    (info) => {
      onViewTypeChange?.(info.view.type);
      fetchEvents(info);
    },
    [fetchEvents, onViewTypeChange]
  );

  useEffect(() => {
    if (activeViewType !== DAY_GRID_MONTH) {
      setExpandedMonthDays({});
    }
  }, [activeViewType]);

  useEffect(() => {
    if (activeViewType !== DAY_GRID_MONTH || !calendarContainerRef.current) return;

    applyMonthDayExpansion({
      container: calendarContainerRef.current,
      expandedMonthDays,
      setExpandedMonthDays,
    });
  }, [filteredEvents, expandedMonthDays, activeViewType]);

  return (
    <Box
      className="calendar-options-container"
      ref={calendarContainerRef}
      sx={getCalendarOptionsSx({
        isDayView: activeViewType === TIME_GRID_DAY,
      })}
    >
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, listPlugin, timeGridPlugin]}
        customButtons={{
          dayWeekday: {
            text: activeViewType === TIME_GRID_DAY ? dayViewDateLabel.weekday : '',
            click: () => {},
          },
          dayDate: {
            text: activeViewType === TIME_GRID_DAY ? dayViewDateLabel.dayOfMonth : '',
            click: () => {},
          },
        }}
        allDayContent={renderAllDayIcon}
        buttonText={{
          today: 'Today',
          month: 'Month',
          week: 'Week',
          day: 'Day',
          list: 'List',
        }}
        dayHeaders={activeViewType !== TIME_GRID_DAY}
        dayHeaderContent={(arg) => {
          if (activeViewType === TIME_GRID_WEEK) {
            return renderWeekViewDayHeader(arg.date);
          }
          return arg.text;
        }}
        height="100%"
        initialView={activeViewType}
        // Appointments are a point in time; a small nonzero default keeps them visible in timeGrid
        // views without letting a late-night event bleed its default 1hr duration into the next day.
        defaultTimedEventDuration="00:15:00"
        scrollTime="08:00:00"
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          meridiem: 'short',
        }}
        slotLabelFormat={{
          hour: 'numeric',
          hour12: true,
          meridiem: 'short',
          omitZeroMinute: true,
        }}
        slotLabelContent={(arg) => fHoursMinutes(arg.date, 'h a')}
        headerToolbar={{
          start: activeViewType === TIME_GRID_DAY ? 'dayWeekday dayDate' : '',
          center: '',
          end: 'today prev,next title',
        }}
        eventDisplay={activeViewType === DAY_GRID_MONTH ? 'block' : 'auto'}
        displayEventTime={activeViewType === LIST_WEEK}
        events={filteredEvents}
        eventContent={renderEventContent}
        datesSet={handleDatesSet}
        eventDidMount={(mountInfo) => {
          mountInfo.el.style.setProperty('--fc-event-hover-color', mountInfo.event.extendedProps.hoverColor);

          if (mountInfo.view.type === LIST_WEEK) {
            const { statusColor, hoverColor } = mountInfo.event.extendedProps;
            const listTimeCell = mountInfo.el.querySelector('.fc-list-event-time');
            const listDateKey = mountInfo.event.start ? mountInfo.event.start.toISOString().slice(0, 10) : 'no-date';
            const previousListEventRow = mountInfo.el.previousElementSibling;
            const previousListDateKey = previousListEventRow?.getAttribute('data-list-date-key');
            const isFirstListEventRow = !previousListEventRow;
            const shouldShowDateLabel = previousListDateKey !== listDateKey;

            mountInfo.el.style.setProperty('--fc-list-event-bg', statusColor);
            mountInfo.el.style.setProperty('--fc-list-event-hover', hoverColor);
            mountInfo.el.setAttribute('data-list-date-key', listDateKey);
            mountInfo.el.setAttribute('data-list-group-start', shouldShowDateLabel ? 'true' : 'false');
            mountInfo.el.setAttribute('data-list-first-row', isFirstListEventRow ? 'true' : 'false');

            if (listTimeCell) {
              const dayLabel = shouldShowDateLabel ? formatListDayLabel(mountInfo.event.start) : '';
              listTimeCell.dataset.dayLabel = dayLabel;
              listTimeCell.textContent = '';
            }
          }
        }}
        eventClick={handleEventClick}
        eventMouseEnter={handleEventMouseEnter}
        eventMouseLeave={handleEventMouseLeave}
        slotEventOverlap={false}
      />
      <CalendarEventTooltip tooltip={hoverTooltip} tooltipWidthPx={TOOLTIP_WIDTH_PX} />
      {loading && (
        <Box className="calendar-loading-overlay" sx={{ justifySelf: 'center' }}>
          <LoadingAnimation />
        </Box>
      )}
    </Box>
  );
}
